<script setup lang="ts">
import ProjectsController from '@controllers/Banks/ProjectsController'
import { router } from '@inertiajs/vue3'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProjectSuggestionData = App.Data.Bank.ProjectSuggestionData

const { t, n } = useI18n()

const props = withDefaults(
  defineProps<{
    projects: Array<ProjectSuggestionData>
    inNegotiations?: boolean
    actions?: {
      details?: boolean
      engage?: boolean
    }
  }>(),
  {
    inNegotiations: false,
  },
)

function updateProjectSuggestion(projectSuggestionId: string) {
  router.put(ProjectsController.update(projectSuggestionId), {
    status: 'proposed',
  })
}
const actionsAvailable = computed(() => {
  return props.actions?.details || props.actions?.engage
})

function projectDetailsUrl(projectId: string) {
  return (props.inNegotiations ? ProjectsController.showNegotiation : ProjectsController.showRequest)(projectId).url
}
</script>

<template>
  <DataTable :value="projects" striped-rows responsive-layout="scroll">
    <Column field="preview" :header="t('columns.preview')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        <Link :href="projectDetailsUrl(data.id)">
          <div>
            <img
              v-if="data.attachments?.find((attachment) => attachment.type === 'photo.project_preview')"
              :src="data.attachments?.find((attachment) => attachment.type === 'photo.project_preview')?.path"
              class="h-full w-full object-cover"
              alt="Preview" />
            <div v-else class="flex items-center justify-center">
              <div class="bg-surface-200 text-surface-500 flex size-10 items-center justify-center rounded">
                <i class="pi pi-image" />
              </div>
            </div>
          </div>
        </Link>
      </template>
    </Column>

    <Column field="status" :header="t('columns.status')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        {{ t(`enums.projectSuggestionStateShort.${data.status}`) }}
      </template>
    </Column>

    <Column field="city" :header="t('columns.city')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        {{ [...new Set(data.estate_objects.map((eo) => eo.city))].join(', ') }}
      </template>
    </Column>

    <Column field="project_type" :header="t('columns.project_type')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        {{ [...new Set(data.estate_objects.map((eo) => eo.estate_object_type))].join(', ') }}
      </template>
    </Column>

    <Column field="customer_type" :header="t('columns.customer_type')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        {{ [...new Set(data.estate_objects.map((eo) => eo.owner_type))].join(', ') }}
      </template>
    </Column>

    <Column field="project.invest_volume" :header="t('columns.project_volume')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        {{ n(data.project.invest_volume, 'currency') }}
      </template>
    </Column>

    <Column v-if="actionsAvailable" :header="t('columns.actions')">
      <template #body="{ data }: { data: ProjectSuggestionData }">
        <div class="flex gap-2">
          <Link v-if="actions?.details" :href="projectDetailsUrl(data.id)">
            <Button :label="t('actions.details')" severity="secondary" />
          </Link>
          <Button
            v-if="actions?.engage && data?.status === 'created'"
            :label="t('actions.propose')"
            severity="primary"
            @click="updateProjectSuggestion(data.id)" />
        </div>
      </template>
    </Column>
    <template #empty>
      {{ t('empty') }}
    </template>
  </DataTable>
</template>

<i18n>
{
    "en": {
        "views": {
            "table": "Table View",
            "cards": "Card View"
        },
        "title": "Active projects",
        "columns": {
            "preview": "Preview",
            "status": "Status",
            "city": "City",
            "buying_price": "Buying Price",
            "project_type": "Project Type",
            "project_volume": "Project Volume",
            "customer_type": "Customer Type",
            "customer_equity": "Equity",
            "actions": "Actions",
            "dsti": "Debt Service-to-Income (DSTI)",
            "dti": "Dept-to-Income (DTI)",
            "ltv": "Loan-to-Value (LTV)",
            "lvtv": "Loan-Volume-to-Value (LVTV)",
            "kdf": "Capital Servicing Capabilities"
        },
        "actions": {
            "details": "Details",
            "propose": "Propose"
        },
        "empty": "No project suggestions available."
    },
    "de": {
        "views": {
            "table": "Tabellenansicht",
            "cards": "Kartenansicht"
        },
        "title": "Aktive Projekte",
        "columns": {
            "preview": "Vorschau",
            "status": "Status",
            "city": "Stadt",
            "buying_price": "Kaufpreis",
            "project_type": "Projektart",
            "project_volume": "Projektvolumen",
            "customer_type": "Kundenart",
            "customer_equity": "Eigenkapital",
            "actions": "Aktionen",
            "dsti": "Kapitaldienstquote (DSTI)",
            "dti": "Verschuldungsquote (DTI)",
            "ltv": "Beleihungsauslauf (LTV)",
            "lvtv": "Darlehensvol. zum Wert (LVTV)",
            "kdf": "Kapitaldienstfähigkeit (KDF)"
        },
        "actions": {
            "details": "Details",
            "propose": "Vorschlagen"
        },
        "empty": "Keine Projektvorschläge verfügbar."
    }
}
</i18n>
